/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import {
  useApiClient,
  useFetchImmutable,
  ComboBoxOption,
} from '@marageti/z4-lib';

export interface CountrySelectorProps {
  label?: string | undefined
  value: string | undefined
  onChange: any;
  [x: string]: any;
}

const TOP_5_COUNTRIES:{ [x: string]: any } = {
  US: { index: 0 },
  AU: { index: 1 },
  CA: { index: 2 },
  SG: { index: 3 },
  'GB-ENG': { index: 4 },
};

type Ref = HTMLInputElement | null;

const formatCountryOptions = (obj: any): ComboBoxOption[] => {
  if (!obj) {
    return [{ label: 'loading', value: '' }];
  }
  const top5: ComboBoxOption[] = new Array(5);
  const ret: ComboBoxOption[] = [];
  Object.keys(obj).forEach((key) => {
    const opt = {
      label: obj[key],
      value: key,
    };
    if (TOP_5_COUNTRIES[key]) {
      const { index } = TOP_5_COUNTRIES[key];
      top5[index] = opt;
      return;
    }
    ret.push(opt);
  });
  return [...top5, ...ret];
};

const CountrySelector = forwardRef<Ref, Omit<CountrySelectorProps, 'ref'>>(({
  label = undefined,
  value,
  onChange,
  ...otherProps
}, ref) => {
  const apiClient = useApiClient();
  const { data: countryCodes, isLoading } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);

  const countryOptions = useMemo(() => (
    formatCountryOptions(countryCodes)
  ), [countryCodes]);

  const handleChange = (_: any, option: any) => {
    onChange(option?.value || '');
  };

  const valueToOption = (countryCode: string | undefined) => {
    const countryOption = countryOptions.find((option) => option?.value === countryCode);
    return countryOption || null;
  };

  return (
    <Autocomplete
      ref={ref}
      id="country-selector"
      value={valueToOption(value)}
      loading={isLoading}
      options={countryOptions}
      onChange={handleChange}
      isOptionEqualToValue={(a, b) => (a.value === b.value)}
      renderInput={(params) => (
        <TextField
          label={label || 'Country'}
          {...params}
          {...otherProps}
        />
      )}
      fullWidth
    />
  );
});

export default CountrySelector;
